import React, { useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getInvestigationTableConfig } from "@/config/tableConfigs/investigationTableConfig";
import { investigationService } from "@/services/investigationService";
import { InvestigationType } from "@/types/db/investigations";
import useAuth from "@/hooks/useAuth";
import { FromDB } from "@/types";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteInvestigationModal } from "@/components/Modals/DeleteInvestigationModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function InvestigationsPage() {
  const { userRoles, displayNames } = useAuth();
  useSetPageTitle(displayNames?.investigations?.plural);
  const tableConfig = getInvestigationTableConfig(userRoles, displayNames);

  // Check if user has investigations read permission
  const hasInvestigationsAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.investigations?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<InvestigationType>> = useMemo(
    () => ({
      storageKey: "investigation_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteInvestigationModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("investigation_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasInvestigationsAccess) {
    return <NoPermissionScreen entityName={displayNames?.investigations?.plural || "Investigations"} />;
  }

  return (
    <EntityTablePage<FromDB<InvestigationType>>
      {...tableConfig}
      fetchData={investigationService.getInvestigations}
      updateEntities={investigationService.saveRowPatches}
      deleteEntities={investigationService.deleteInvestigationsByIds}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
